import React, { useState } from 'react';
import { styled } from '@glitz/react';
import { H2, Paragraph, TextBlockSmall, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import Reviews from '../Reviews';
import { Favourite16x16, FavouriteFull16x16 } from 'Shared/Icons/Favorite';
import ProductTags from '../ProductTags';
import Viewport from 'Shared/Viewport';
import ProductActivity from '../Models/ProductActivity.type';
import ProductActivities from '../ProductActivities';
import Description from '../Models/Description.type';
import ManufacturingInformation from '../Models/ManufacturingInformation.type';
import Html from 'Shared/Partials/PropertyTypes/Html';
import FeedbackButton, { ButtonTheme } from 'Shared/Button/Feedback';
import { SolidButton } from 'Shared/TextButtons';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import * as style from 'Shared/Style';
import A from 'Shared/Generic/A';

type ExtendedDataLabelsType = {
  descriptionLabel: string;
  reviewsLabel: string;
  saveToWishlistLabel: string;
  compareProductLabel: string;
  readMoreLabel: string;
  readLessLabel: string;
  manufacturingCountryLabel: string;
  manufacturingFactoryLabel: string;
  itemNumber: string;
};

type ExtendedDataType = {
  labels: ExtendedDataLabelsType;
  rating: number;
  numberOfReviews: number;
  productTitle: string;
  productTags: string[];
  productDescription: Description;
  manufacturingInformation: ManufacturingInformation;
  itemNumber: string;
  onSaveToWishlist: (e: any) => Promise<void>;
  onCompareProduct: () => Promise<void>;
  productActivities: ProductActivity[];
  isFavorited?: boolean;
  loggedIn?: boolean;
  scrollToRef?: React.MutableRefObject<any>;
};

const DescriptionWrap = styled.div({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  transition: {
    property: 'max-height',
    duration: 300,
  },
})

const DescriptionBodyWrap = styled(Html, {
  paddingTop: '16px',
  transition: {
    property: 'max-height',
    duration: 300,
  },
})

const DescriptionBody = styled.p({
  fontSize: '16px',
  fontWeight: 450,
  lineHeight: '110%',
});

const ManufacturingWrap = styled.div({
  paddingTop: '16px',
  display: 'flex',
  flexDirection: 'column',
});
const ManufacturingData = styled.div({
  display: 'flex',
  gap: '8px'
});

const ItemNumberWrap = styled.div({
  paddingTop: '16px',
  display: 'flex',
  flexDirection: 'column',
});
const ItemNumberData = styled.div({
  display: 'flex',
  gap: '8px'
});

const Heading = styled(H2, {
  margin: { top: '16px', bottom: '24px' },
});

const ReadMore = styled.span({
  display: 'block',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '14px',
  letterSpacing: '0.01em',
  margin: { xy: '0px' },
  color: style.colors.monochrome.a11ygrey,
  cursor: 'pointer',
  lineHeight: '100%',
  textTransform: 'uppercase',
  marginTop: '16px',
});

const FavStyled = styled(Favourite16x16, {
  marginLeft: '8px'
})

const FavFullStyled = styled(FavouriteFull16x16, {
  marginLeft: '8px'
})

const ButtonWrap = styled.div({
  display: 'flex',
  gap: '8px',
  margin: { top: '40px' },
  flexDirection: 'column',
  ['@media ' + style.mediaMinLarge]: {
    flexDirection: 'row',
  },
})

const ProductActivitiesWrap = styled.div({
  ['@media ' + style.mediaUpToLarge]: {
    marginTop: '40px',
  },
})

const ProductDescriptionIntro = styled(Paragraph, {
  marginTop: '24px'
})

function ExtendedData(props: ExtendedDataType) {
  const FavoIcon = props.isFavorited ? FavFullStyled : FavStyled;
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  return (
    <Viewport>
      {(isCompact: boolean) => (
        <div className={createGridClasses(12, 11, isCompact ? '' : 'offset-lg-2 grid subgrid-11')}>
          <div ref={props.scrollToRef} className={createGridClasses(12, 11)}>
            {!isCompact && <Reviews labels={{ description: props.labels.descriptionLabel, reviews: props.labels.reviewsLabel }} rating={props.rating} numberOfReviews={props.numberOfReviews} />}
            {isCompact && <TextBlockSmall upperCase>{props.labels.descriptionLabel}</TextBlockSmall>}
            <Heading fontSize={ResponsiveSize.D64_M40}>{props.productTitle}</Heading>
          </div>
          <div className={createGridClasses(12, 6)}>
            {props.productTags && <ProductTags tags={props.productTags} />}
            {props.productDescription && (
              <>
                {props.productDescription.intro && <ProductDescriptionIntro fontSize={ResponsiveSize.D32_M24}>{props.productDescription.intro}</ProductDescriptionIntro>}
                {(props.productDescription.body || props.manufacturingInformation) && <>
                  <DescriptionWrap css={{ maxHeight: descriptionExpanded ? '100vh' : '0px', }}>
                    {props.productDescription.body && <DescriptionBodyWrap
                      component={DescriptionBody}
                      html={props.productDescription.body.html}
                    />}
                    {props.itemNumber && <ItemNumberWrap>
                      <ItemNumberData><span>{props.labels.itemNumber}:</span><span>{props.itemNumber}</span></ItemNumberData>
                    </ItemNumberWrap>
                    }
                    {props.manufacturingInformation && <ManufacturingWrap>
                      <ManufacturingData><span>{props.labels.manufacturingCountryLabel}:</span><span>{props.manufacturingInformation.country}</span></ManufacturingData>
                      <ManufacturingData><span>{props.labels.manufacturingFactoryLabel}:</span><span>{props.manufacturingInformation.factory}</span></ManufacturingData>
                      {props.manufacturingInformation.infoUrl && <ManufacturingData><A to={props.manufacturingInformation.infoUrl} useDefaultLinkColor={true}>{props.labels.readMoreLabel}</A></ManufacturingData>}
                    </ManufacturingWrap>
                    }
                  </DescriptionWrap>
                  <ReadMore onClick={() => { setDescriptionExpanded(!descriptionExpanded); }}>
                    {descriptionExpanded ? props.labels.readLessLabel : props.labels.readMoreLabel}
                  </ReadMore>
                </>}
              </>
            )}
          </div>
          {props.productActivities && props.productActivities.length > 0 &&
            <ProductActivitiesWrap className={createGridClasses(12, 3, createOffsetGridClassLarge(8))} >
              <ProductActivities productActivities={props.productActivities} />
            </ProductActivitiesWrap>
          }
          <ButtonWrap className={createGridClasses(12, 6)}>
            {props.labels.saveToWishlistLabel && props.loggedIn && (
              <FeedbackButton
                onClick={props.onSaveToWishlist}
                iconAfter={<FavoIcon />}
                theme={ButtonTheme.WhiteToBlack}
                successText={props.labels.saveToWishlistLabel}
                pendingText={props.labels.saveToWishlistLabel}
              >
                {props.labels.saveToWishlistLabel}
              </FeedbackButton>
            )}
            {props.labels.saveToWishlistLabel && !props.loggedIn && (
              <SolidButton
                onClick={props.onSaveToWishlist}
                iconAfter={<FavoIcon />}
                theme={ButtonTheme.WhiteToBlack}
              >
                {props.labels.saveToWishlistLabel}
              </SolidButton>
            )}

            {/* Disabled for now. {props.labels.compareProductLabel && (
              <FeedbackButton
                height={{ desktop: 48, mobile: 40 }}
                padding={{ x: 23 }}
                text={props.labels.compareProductLabel}
                onClick={props.onCompareProduct}
                iconAfter={<Compare css={{ marginLeft: '8px', width: '12px' }} />}
                theme={ButtonTheme.WhiteToBlack}
              />
            )} */}
          </ButtonWrap>
        </div>
      )}
    </Viewport>
  );
}

export default ExtendedData;
